import { useMemo } from 'react';

import dayjs from 'dayjs';
import { useSearchParams } from 'react-router';
import { RRuleSet, rrulestr } from 'rrule';

import type { Event, OfficeType, PartialUser } from '../../Data/types';
import type { Gec_Events } from '../../gql/graphql';
import { generateUserImageUrl } from '../../helpers/userImagesUrlUtils';

type UserLike = {
  ActiveDirectoryLogin?: string | null;
  NamePreferredDisplay?: string | null;
  Email?: string | null;
  TitleFullyQualified?: string | null;
  PrimaryPracticeGroup?: {
    SubSubPracticeGroupName?: string | null;
    PracticeGroupName?: string | null;
  } | null;
  BusinessPrimaryOfficePhoneNumberFull?: string | null;
  BusinessPrimaryOfficeAddress1?: string | null;
  BusinessPrimaryOffice?: string | null;
} | null;

type OfficeLike = {
  office?: {
    id: string;
    office_code: string;
    name: string;
    address: string;
  } | null;
};

const mapPartialUser = (user?: UserLike): PartialUser => ({
  ADSystemID: user?.ActiveDirectoryLogin || undefined,
  DisplayName: user?.NamePreferredDisplay || undefined,
  ImageUrlLarge: user?.Email ? generateUserImageUrl(user.Email, 'L') : undefined,
  ImageUrlMedium: user?.Email ? generateUserImageUrl(user.Email, 'M') : undefined,
  ImageUrlSmall: user?.Email ? generateUserImageUrl(user.Email, 'S') : undefined,
  WorkEmail: user?.Email || undefined,
  DisplayTitle: user?.TitleFullyQualified || undefined,
  PracticeGrpName: user?.PrimaryPracticeGroup?.SubSubPracticeGroupName || undefined,
  DirectWorkPhone: user?.BusinessPrimaryOfficePhoneNumberFull || undefined,
  PeopleOfficeAddress: user?.BusinessPrimaryOfficeAddress1 || undefined,
  OfficeName: user?.BusinessPrimaryOffice || undefined,
  PracticeGrpRollupName: user?.PrimaryPracticeGroup?.PracticeGroupName || undefined,
});

const mapOfficeLinks = (links?: OfficeLike[] | null): OfficeType[] | null => {
  if (!links?.length) {
    return null;
  }

  return links
    .map((link) =>
      link.office
        ? {
            id: link.office.id,
            office_code: link.office.office_code,
            name: link.office.name,
            address: link.office.address,
          }
        : undefined,
    )
    .filter((office): office is OfficeType => office !== undefined);
};

const buildRecurrenceSummary = (event: Gec_Events): Event['recurrence'] => {
  if (!event.is_recurring || !event.recurrence_pattern) {
    return null;
  }

  const ruleSet = new RRuleSet();
  ruleSet.rrule(rrulestr(event.recurrence_pattern));
  const originalStart = dayjs(ruleSet._rrule[0].options.dtstart);

  return {
    startDate: originalStart,
    endDate: event.is_all_day ? originalStart : originalStart.add(event.duration!, 'minutes'),
    recurrencePattern: event.recurrence_pattern,
    duration: event.duration,
    exceptions: event.event_exceptions.map((e) => ({
      id: e.id,
      exceptionDate: dayjs(e.exception_date_utc),
    })),
  };
};

// Helper to parse ISO datetime string into dayjs object
const parseISODateTime = (dateTimeStr?: string) => {
  if (!dateTimeStr) return null;
  const [date, time] = dateTimeStr.split('T');
  const [hours, minutes, seconds] = time.split(':');
  return dayjs(date)
    .set('hour', Number.parseInt(hours))
    .set('minute', Number.parseInt(minutes))
    .set('second', Number.parseInt(seconds));
};

// Helper to expand recurring events into individual occurrences
const expandRecurringEvents = (
  event: Gec_Events,
  baseEvent: Event,
  startDate?: string | null,
  endDate?: string | null,
): Event[] => {
  if (!event.recurrence_pattern) return [baseEvent];

  const ruleSet = new RRuleSet();
  ruleSet.rrule(rrulestr(event.recurrence_pattern));
  const ruleStart = ruleSet._rrule[0].options.dtstart;

  event.event_exceptions.forEach((e) => {
    const exceptionDate = dayjs
      .utc(e.exception_date)
      .hour(ruleStart.getUTCHours())
      .minute(ruleStart.getUTCMinutes())
      .second(ruleStart.getUTCSeconds())
      .millisecond(0)
      .toDate();
    ruleSet.exdate(exceptionDate);
  });

  const after = startDate ? dayjs(startDate) : dayjs().subtract(2, 'year');
  const before = endDate ? dayjs(endDate).add(1, 'day') : dayjs().add(2, 'year');
  const timeParts = event.start_date_time.split('T')[1].split(':');

  return ruleSet.between(after.toDate(), before.toDate(), true).map((date) => {
    const dateString = dayjs(date).format('YYYY-MM-DD');
    const recurringStart = dayjs(dateString)
      .set('hour', Number.parseInt(timeParts[0]))
      .set('minute', Number.parseInt(timeParts[1]))
      .set('second', Number.parseInt(timeParts[2]));
    const recurringEnd = event.duration ? recurringStart.add(event.duration, 'minutes') : recurringStart;
    return {
      ...baseEvent,
      eventDateTime: { start: recurringStart, end: recurringEnd },
      recurrence: { ...baseEvent.recurrence!, startDate: recurringStart, endDate: recurringEnd },
    };
  });
};

function useParseEvents(filteredData: Gec_Events[]): Event[] | undefined {
  const [urlFilters] = useSearchParams();
  const startDate = urlFilters.get('startDate');
  const endDate = urlFilters.get('endDate');

  return useMemo(() => {
    const events: Event[] = [];
    filteredData?.forEach((event) => {
      const baseEvent: Event = {
        allDay: event.is_all_day,
        audience: event.audience_in_events.map((aud) => ({
          id: aud.audience?.id,
          name: aud.audience?.name,
        })),
        cle: event.is_cle,
        clientEventOrCelebration: event.is_client_event_or_celebration,
        created_at: event.created_at ? dayjs(event.created_at) : null,
        created_by: event.created_by,
        created_by_user: mapPartialUser(event.created_by_user),
        description: event.event_details || undefined,
        eventDateTime: {
          end: parseISODateTime(event.end_date_time),
          start: parseISODateTime(event.start_date_time),
        },
        eventContact: event.contact_in_events?.map((contact) => mapPartialUser(contact.user)) || null,
        eventFormat: event.event_format,
        eventType: {
          id: event.event_type?.id || 5,
          event_type: event.event_type?.event_type || 'Other',
          hosting_type: event.event_type?.hosting_type || null,
          color: event.event_type?.color || null,
        },
        eventTypeOther: event.event_type_other || undefined,
        timezone: event.time_zone || 'UTC',
        isLocationExternal: Boolean(event.external_office),
        externalLocation: event.external_office?.toString(),
        firmInitiatives: event.firm_initiatives_in_event.map((fi) => ({
          id: fi.firm_initiatives?.id,
          name: fi.firm_initiatives?.name,
        })),
        id: event.id,
        industries: event.industries_in_event.map((i) => ({
          id: i.industries?.id,
          name: i.industries?.name,
        })),
        departments: event.departments_in_event.map((d) => ({
          id: d.departments?.id,
          name: d.departments?.name,
        })),
        inviteOnly: event.is_invite_only,
        hostingType: event.hosting_type || undefined,
        isRecurring: event.is_recurring,
        mlSpeaker: event.speaker_in_events?.map((speaker) => mapPartialUser(speaker.user)) || null,
        officeLocation: mapOfficeLinks(event.offices_in_events as OfficeLike[]),
        officeVisibility: mapOfficeLinks(event.office_visibility_in_events as OfficeLike[]),
        isOutsideSpeaker: event.outside_speakers.length > 0,
        outsideSpeakers: event.outside_speakers.map((os) => ({
          id: os.id,
          name: os.name,
          title: os.title || '',
          organization: os.organization || '',
        })),
        practiceGroups: event.practice_groups_in_event.map((pg) => ({
          id: pg.practice_groups?.id,
          name: pg.practice_groups?.name,
        })),
        recurrence: buildRecurrenceSummary(event),
        sponsoringAttorney:
          event.sponsoring_attorney_in_events?.map((attorney) => mapPartialUser(attorney.user)) || null,
        sponsorOrganization: event.sponsoring_orgnization || undefined,
        hostingGroupOther: event.hosting_group_other || undefined,
        title: event.title,
        updated_at: event.updated_at ? dayjs(event.updated_at) : null,
        updated_by: event.updated_by || undefined,
        updated_by_user: mapPartialUser(event.updated_by_user),
        uuid: event.uuid,
      };
      // Expand recurring events or add single event
      expandRecurringEvents(event, baseEvent, startDate, endDate).forEach((e) => events.push(e));
    });
    return events;
  }, [filteredData, startDate, endDate]);
}

export default useParseEvents;
